import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { brandsOpenApi, COLORS, ROUTE_PATHS } from '@constants';
import { useStore } from '@hooks';
import { Icon, Loader, Typography } from '@ui';
import { axiosBoa } from '@utils';
import { Flex, notification } from 'antd';
import { usePreWizardContext } from '../../PreWizardContext';
import { Container, ScrollContainer, StyledListContainer, StyledListItem } from './styled';

const MetaCatalogs = () => {
    const navigate = useNavigate();
    const store = useStore();
    const { getAccountSyncProgress } = store.accountsStore;
    const { setIsButtonEnabled, setOnButtonClick, setIsSubmitting } = usePreWizardContext();
    const [catalogs, setCatalogs] = useState([]);
    const [selectedCatalogIds, setSelectedCatalogIds] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchCatalogs();
    }, []);

    const fetchCatalogs = async () => {
        try {
            setLoading(true);
            const response = await axiosBoa.get(brandsOpenApi.feedOauth.metaCatalogs.list);

            if (response.catalogs) {
                setCatalogs(response.catalogs);
                const selected = response.catalogs.filter((cat) => cat.isSelected).map((cat) => cat.id);
                setSelectedCatalogIds(selected);
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to load Meta catalogs. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCatalogToggle = (catalogId) => {
        setSelectedCatalogIds((prev) => {
            if (prev.includes(catalogId)) {
                return prev.filter((id) => id !== catalogId);
            } else {
                return [...prev, catalogId];
            }
        });
    };

    const handleSyncCatalogs = useCallback(async () => {
        if (selectedCatalogIds.length === 0) {
            notification.warning({
                message: 'Select catalogs',
                description: 'Please select at least one catalog to sync.'
            });
            return;
        }

        try {
            setIsSubmitting(true);
            await axiosBoa.post(brandsOpenApi.feedOauth.metaCatalogs.sync, {
                catalogIds: selectedCatalogIds
            });
            await getAccountSyncProgress();

            navigate(ROUTE_PATHS.PRODUCTS);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to start catalog sync. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedCatalogIds, navigate, setIsSubmitting, getAccountSyncProgress]);

    useEffect(() => {
        setIsButtonEnabled(selectedCatalogIds.length > 0);
    }, [selectedCatalogIds, setIsButtonEnabled]);

    useEffect(() => {
        setOnButtonClick(() => handleSyncCatalogs);
    }, [handleSyncCatalogs, setOnButtonClick]);

    if (loading) {
        return (
            <Flex vertical style={{ flexGrow: 1 }} justify='center' align='center'>
                <Loader size='80px' />
            </Flex>
        );
    }

    return (
        <Flex vertical style={{ flexGrow: 1 }}>
            <Container>
                <Typography.Title
                    level={1}
                    color={COLORS.$gray100Black}
                    style={{ marginBottom: '16px' }}
                    data-e2e='connection-via-meta_title'
                >
                    Connection via Meta Commerce Catalog
                </Typography.Title>

                <Typography.Text
                    style={{ marginBottom: '24px', display: 'block' }}
                    color={COLORS.$gray60Black}
                    type='body2'
                    data-e2e='connection-via-meta_description'
                >
                    Sync your catalog from Meta Business Suite
                </Typography.Text>

                <StyledListContainer>
                    <Flex gap={24} align='center'>
                        <Icon color={COLORS.$gray40Black} size='80px' component={() => <Icon.EntireCatalogIcon />} />
                        <Flex vertical gap={8}>
                            <Typography.Text type='badgeLarge'>Meta Catalog</Typography.Text>
                            <Typography.Text type='descriptor1'>
                                Select at least 1 catalog to sync with Klickly
                            </Typography.Text>
                        </Flex>
                    </Flex>

                    {catalogs.length === 0 ? (
                        <Flex vertical align='center' justify='center' style={{ padding: '40px 0', marginTop: '24px' }}>
                            <Typography.Text type='body2' color={COLORS.$gray60Black}>
                                No catalogs found in your Meta Business account.
                            </Typography.Text>
                            <Typography.Text
                                type='descriptor1'
                                color={COLORS.$gray40Black}
                                style={{ marginTop: '8px' }}
                            >
                                Please create a catalog in Meta Commerce Manager first.
                            </Typography.Text>
                        </Flex>
                    ) : (
                        <div style={{ maxHeight: 250, overflow: 'hidden', marginTop: '24px' }}>
                            <ScrollContainer>
                                <Flex vertical style={{ paddingRight: 10 }}>
                                    {catalogs.map((item) => (
                                        <StyledListItem
                                            onClick={() => handleCatalogToggle(item.id)}
                                            key={item.id}
                                            style={{ cursor: 'pointer' }}
                                            data-e2e='connection-via-meta_catalog_card'
                                        >
                                            <Flex align='center' justify='space-between'>
                                                <Flex vertical gap={4}>
                                                    <Typography.Text type='descriptor1'>{item.name}</Typography.Text>
                                                </Flex>
                                                {selectedCatalogIds.includes(item.id) && (
                                                    <Icon.CheckIcon width='24px' height='24px' color={COLORS.$pink} />
                                                )}
                                            </Flex>
                                        </StyledListItem>
                                    ))}
                                </Flex>
                            </ScrollContainer>
                        </div>
                    )}
                </StyledListContainer>
            </Container>
        </Flex>
    );
};

export default MetaCatalogs;
